import React, { useCallback } from 'react';
import { SaveTelemetry, TelemetryStatus } from '#types/types';
import { X, CheckCircle, XCircle, AlertCircle, Loader, Copy } from '@/components/ui/lucide-memo';
import { toast } from 'sonner';
import { useKernel } from '@/kernel/Kernel';
import { useKernelStore } from '@/kernel/kernelStores';
import { copyTextToClipboard } from '#lib/utils';

const translations = {
  en: {
    title: 'Save Process Status',
    close: 'Close',
    copyReport: 'Copy Report',
    reportCopied: 'Save report copied to clipboard.',
    reportCopyFailed: 'Failed to copy report.',
    overallStatus: 'Overall Status',
    totalDuration: 'Total Duration',
    saveAttemptId: 'Save Attempt ID',
    stepName: 'Step',
    status: 'Status',
    duration: 'Duration (ms)',
    errorDetails: 'Error Details',
    noTelemetry: 'No save telemetry data available.',
    statusMap: {
      PENDING: 'Pending',
      RUNNING: 'Running',
      SUCCESS: 'Success',
      FAILED: 'Failed',
      CANCELLED: 'Cancelled',
    },
  },
  fr: {
    title: 'Statut du processus de sauvegarde',
    close: 'Fermer',
    copyReport: 'Copier le rapport',
    reportCopied: 'Rapport de sauvegarde copie dans le presse-papiers.',
    reportCopyFailed: 'Echec de la copie du rapport.',
    overallStatus: 'Statut general',
    totalDuration: 'Duree totale',
    saveAttemptId: 'ID de la tentative de sauvegarde',
    stepName: 'Etape',
    status: 'Statut',
    duration: 'Duree (ms)',
    errorDetails: 'Details de l\'erreur',
    noTelemetry: 'Aucune donnee de telemetrie de sauvegarde disponible.',
    statusMap: {
      PENDING: 'En attente',
      RUNNING: 'En cours',
      SUCCESS: 'Succes',
      FAILED: 'Echec',
      CANCELLED: 'Annule',
    },
  },
};

const StatusIcon = ({ status }: { status: TelemetryStatus }) => {
  switch (status) {
    case 'SUCCESS':
      return <CheckCircle size={16} className="text-green-500" />;
    case 'FAILED':
      return <XCircle size={16} className="text-red-500" />;
    case 'CANCELLED':
      return <AlertCircle size={16} className="text-yellow-500" />;
    case 'RUNNING':
      return <Loader size={16} className="animate-spin text-blue-400" />;
    case 'PENDING':
    default:
      return <div className="w-4 h-4" />;
  }
};

const getStatusColorClass = (status: TelemetryStatus) => {
  switch (status) {
    case 'SUCCESS':
      return 'text-green-400';
    case 'FAILED':
      return 'text-red-400';
    case 'CANCELLED':
      return 'text-yellow-400';
    case 'RUNNING':
      return 'text-blue-400';
    case 'PENDING':
    default:
      return 'text-neutral-400';
  }
};

interface SaveStatusModalProps {
  lang?: 'en' | 'fr';
}

const SaveStatusModal: React.FC<SaveStatusModalProps> = ({ lang }) => {
  const { kernel } = useKernel();
  const storeLang = useKernelStore((state) => state.language);
  const resolvedLang = lang ?? storeLang;
  const t = translations[resolvedLang];
  const telemetry = kernel.current?.getSaveTelemetry();

  const handleClose = useCallback(() => {
    kernel.current?.closeModal();
  }, [kernel]);

  const formatTelemetryForCopy = (telemetryData: SaveTelemetry): string => {
    let report = `--- Save Process Report ---\n`;
    report += `ID: ${telemetryData.saveAttemptId}\n`;
    report += `Status: ${t.statusMap[telemetryData.status]}\n`;
    report += `Start Time: ${new Date(telemetryData.startTime).toISOString()}\n`;
    if (telemetryData.endTime) {
      report += `End Time: ${new Date(telemetryData.endTime).toISOString()}\n`;
      report += `Total Duration: ${(telemetryData.endTime - telemetryData.startTime) / 1000}s\n`;
    }
    report += `\n--- Steps ---\n`;

    telemetryData.steps.forEach((step) => {
      report += `\nStep: ${step.name}\n`;
      report += `  Status: ${t.statusMap[step.status]}\n`;
      if (step.durationMs !== null) {
        report += `  Duration: ${step.durationMs} ms\n`;
      }
      if (step.error) {
        report += `  Error: ${step.error}\n`;
      }
      if (Object.keys(step.metadata).length > 0) {
        report += `  Metadata: ${JSON.stringify(step.metadata)}\n`;
      }
    });

    return report;
  };

  const handleCopyReport = useCallback(async () => {
    if (!telemetry) {
      return;
    }

    try {
      const reportText = formatTelemetryForCopy(telemetry);
      await copyTextToClipboard(reportText);
      toast.success(t.reportCopied);
    } catch (err) {
      console.error('Failed to copy telemetry report:', err);
      toast.error(t.reportCopyFailed);
    }
  }, [telemetry, t]);

  if (!telemetry) {
    return (
      <div className="fixed inset-0 z-501 flex items-center justify-center bg-black/70 backdrop-blur-sm">
        <div className="bg-neutral-800 text-white rounded-lg shadow-xl w-full max-w-lg p-6 animate-fade-in">
          <p>{t.noTelemetry}</p>
          <div className="mt-4 flex justify-end">
            <button
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-neutral-300 bg-neutral-600 hover:bg-neutral-500 rounded-md"
            >
              {t.close}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const totalDuration = telemetry.endTime
    ? `${((telemetry.endTime - telemetry.startTime) / 1000).toFixed(2)}s`
    : 'In progress...';

  return (
    <div className="fixed inset-0 z-501 flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <div className="bg-neutral-800 text-white rounded-lg shadow-xl w-full max-w-2xl p-6 animate-fade-in overflow-hidden flex flex-col max-h-[90vh]">
        <div className="flex justify-between items-center mb-4 flex-shrink-0">
          <h2 className="text-xl font-semibold">{t.title}</h2>
          <button onClick={handleClose} className="text-neutral-400 hover:text-white">
            <X size={20} />
          </button>
        </div>

        <div className="grid grid-cols-2 gap-x-4 gap-y-2 mb-4 text-sm text-neutral-300 flex-shrink-0">
          <div>
            <strong>{t.overallStatus}:</strong>{' '}
            <span className={getStatusColorClass(telemetry.status)}>{t.statusMap[telemetry.status]}</span>
          </div>
          <div>
            <strong>{t.totalDuration}:</strong> {totalDuration}
          </div>
          <div className="col-span-2">
            <strong>{t.saveAttemptId}:</strong>{' '}
            <span className="font-mono text-xs">{telemetry.saveAttemptId}</span>
          </div>
        </div>

        <div className="overflow-y-auto custom-scrollbar flex-grow pr-2">
          <table className="w-full text-sm text-left">
            <thead className="text-xs text-neutral-400 bg-neutral-700 sticky top-0">
              <tr>
                <th scope="col" className="px-4 py-2 w-1/12">
                  {t.status}
                </th>
                <th scope="col" className="px-4 py-2 w-5/12">
                  {t.stepName}
                </th>
                <th scope="col" className="px-4 py-2 w-2/12 text-right">
                  {t.duration}
                </th>
              </tr>
            </thead>
            <tbody>
              {telemetry.steps.map((step, index) => (
                <React.Fragment key={index}>
                  <tr className="border-b border-neutral-700 hover:bg-neutral-700/50">
                    <td className="px-4 py-2 flex items-center justify-center">
                      <StatusIcon status={step.status} />
                    </td>
                    <td className={`px-4 py-2 font-medium ${getStatusColorClass(step.status)}`}>{step.name}</td>
                    <td className="px-4 py-2 text-right font-mono">
                      {step.durationMs !== null ? `${step.durationMs}` : '-'}
                    </td>
                  </tr>
                  {step.error && (
                    <tr className="bg-red-900/20">
                      <td colSpan={3} className="px-4 py-1 text-xs">
                        <strong className="text-red-400">{t.errorDetails}:</strong>{' '}
                        <span className="text-neutral-300 font-mono">{step.error}</span>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-6 flex justify-between items-center flex-shrink-0">
          <button
            onClick={handleCopyReport}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-neutral-300 bg-neutral-700 hover:bg-neutral-600 rounded-md"
          >
            <Copy size={14} /> {t.copyReport}
          </button>
          <button
            onClick={handleClose}
            className="px-6 py-2 text-base font-semibold text-white bg-neutral-600 hover:bg-neutral-500 rounded-lg"
          >
            {t.close}
          </button>
        </div>
      </div>
    </div>
  );
};

export default React.memo(SaveStatusModal);

